import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Divider,
  Tabs,
  Tab,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Skeleton,
  Collapse,
  Menu,
  Switch,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Timeline as TimelineIcon,
  Key as KeyIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
  AttachMoney as AttachMoneyIcon,
  Psychology as PsychologyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  AccessTime as AccessTimeIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  MoreVert as MoreVertIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import SyntaxHighlighter from "react-syntax-highlighter";
import {
  atomOneDark,
  atomOneLight,
} from "react-syntax-highlighter/dist/esm/styles/hljs";
import DashboardLayout from "../components/DashboardLayout";
import { useAuth } from "../contexts/AuthContext";
import { deploymentApi, apiKeysApi } from "../services/api";
import { toast } from "sonner";
import RegisterModelForRagDialog from "../components/RegisterModelForRagDialog";
import ScheduleUptimeDialog from "../components/ScheduleUptimeDialog";
import { llmApi } from "../services/rag-service";
import { scheduledUptimeApi } from "../services/omnimizer-api-service";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from "@mui/lab";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import { useThemeMode } from "../contexts/ThemeContext";

// Skeleton components for progressive loading
const DeploymentHeaderSkeleton = () => (
  <Box sx={{ mb: 4 }}>
    <Grid container spacing={2} alignItems="center">
      <Grid item xs>
        <Skeleton variant="text" width="50%" height={40} />
        <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
          <Skeleton
            variant="rounded"
            width={80}
            height={24}
            sx={{ marginInlineEnd: 1 }}
          />
          <Skeleton variant="text" width="30%" height={20} />
        </Box>
      </Grid>
      <Grid item>
        <Box sx={{ display: "flex" }}>
          <Skeleton
            variant="rounded"
            width={100}
            height={36}
            sx={{ marginInlineEnd: 1 }}
          />
          <Skeleton
            variant="rounded"
            width={100}
            height={36}
            sx={{ marginInlineEnd: 1 }}
          />
          <Skeleton variant="rounded" width={100} height={36} />
        </Box>
      </Grid>
    </Grid>
  </Box>
);

const ModelInfoSkeleton = () => (
  <Card>
    <CardContent>
      <Skeleton variant="text" width="40%" height={32} />
      <Divider sx={{ mb: 2 }} />

      {Array.from({ length: 8 }).map((_, index) => (
        <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
          <Grid item xs={4}>
            <Skeleton variant="text" width="80%" height={20} />
          </Grid>
          <Grid item xs={8}>
            <Skeleton
              variant="text"
              width={index % 2 === 0 ? "60%" : "40%"}
              height={20}
            />
          </Grid>
        </Grid>
      ))}
    </CardContent>
  </Card>
);

const ResourcesSkeleton = () => (
  <Card>
    <CardContent>
      <Skeleton variant="text" width="40%" height={32} />
      <Divider sx={{ mb: 2 }} />

      {Array.from({ length: 5 }).map((_, index) => (
        <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
          <Grid item xs={4}>
            <Skeleton variant="text" width="80%" height={20} />
          </Grid>
          <Grid item xs={8}>
            <Skeleton variant="text" width="30%" height={20} />
          </Grid>
        </Grid>
      ))}
    </CardContent>
  </Card>
);

const ApiUsageSkeleton = () => (
  <Card>
    <CardContent>
      <Skeleton variant="text" width="40%" height={32} />
      <Divider sx={{ mb: 2 }} />

      <Skeleton variant="text" width="90%" height={20} sx={{ mb: 1 }} />
      <Skeleton variant="text" width="95%" height={20} sx={{ mb: 1 }} />
      <Skeleton variant="text" width="85%" height={20} sx={{ mb: 3 }} />

      <Skeleton variant="rectangular" height={200} />
    </CardContent>
  </Card>
);

const ApiKeysTableSkeleton = () => {
  const isRTL = i18n.dir() === "rtl";
  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell align={isRTL ? "right" : "left"}>
              <Skeleton variant="text" width="100%" />
            </TableCell>
            <TableCell align={isRTL ? "right" : "left"}>
              <Skeleton variant="text" width="100%" />
            </TableCell>
            <TableCell align={isRTL ? "right" : "left"}>
              <Skeleton variant="text" width="100%" />
            </TableCell>
            <TableCell align={isRTL ? "right" : "left"}>
              <Skeleton variant="text" width="100%" />
            </TableCell>
            <TableCell align={isRTL ? "right" : "left"}>
              <Skeleton variant="text" width="100%" />
            </TableCell>
            <TableCell align={isRTL ? "right" : "left"}>
              <Skeleton variant="text" width="100%" />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from({ length: 3 }).map((_, index) => (
            <TableRow key={index}>
              <TableCell align={isRTL ? "right" : "left"}>
                <Skeleton variant="text" width="80%" />
              </TableCell>
              <TableCell align={isRTL ? "right" : "left"}>
                <Skeleton variant="text" width="90%" />
              </TableCell>
              <TableCell align={isRTL ? "right" : "left"}>
                <Skeleton variant="text" width="60%" />
              </TableCell>
              <TableCell align={isRTL ? "right" : "left"}>
                <Skeleton variant="text" width="70%" />
              </TableCell>
              <TableCell align={isRTL ? "right" : "left"}>
                <Skeleton variant="rounded" width={60} height={24} />
              </TableCell>
              <TableCell align={isRTL ? "right" : "left"}>
                <Skeleton variant="circular" width={30} height={30} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const TimelineEventsSkeleton = () => (
  <Paper>
    <Box sx={{ p: 1 }}>
      <Timeline
        position="right"
        sx={{ p: 0, paddingInlineStart: 0, marginInlineEnd: 2 }}
      >
        {Array.from({ length: 5 }).map((_, index) => (
          <TimelineItem key={index} sx={{ minHeight: "auto", my: 0.5 }}>
            <TimelineOppositeContent
              sx={{
                flex: 0.1,
                pt: "10px",
                fontSize: "0.75rem",
                m: 0,
                paddingInlineEnd: 1,
                paddingInlineStart: 0,
              }}
            >
              <Skeleton variant="text" width="90%" height={14} />
              <Skeleton
                variant="text"
                width="70%"
                height={14}
                sx={{ mt: 0.5 }}
              />
            </TimelineOppositeContent>
            <TimelineSeparator>
              <TimelineDot sx={{ my: 0.5 }}>
                <Skeleton variant="circular" width={20} height={20} />
              </TimelineDot>
              {index < 4 && <TimelineConnector sx={{ height: 40 }} />}
            </TimelineSeparator>
            <TimelineContent sx={{ paddingInlineEnd: 0, py: 0.5 }}>
              <Skeleton
                variant="rectangular"
                width="100%"
                height={40}
                sx={{ borderRadius: 1 }}
              />
            </TimelineContent>
          </TimelineItem>
        ))}
      </Timeline>
    </Box>
  </Paper>
);

const VersionsTableSkeleton = () => {
  const isRTL = i18n.dir() === "rtl";
  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell align={isRTL ? "right" : "left"}>
              <Skeleton variant="text" width="100%" />
            </TableCell>
            <TableCell align={isRTL ? "right" : "left"}>
              <Skeleton variant="text" width="100%" />
            </TableCell>
            <TableCell align={isRTL ? "right" : "left"}>
              <Skeleton variant="text" width="100%" />
            </TableCell>
            <TableCell align={isRTL ? "right" : "left"}>
              <Skeleton variant="text" width="100%" />
            </TableCell>
            <TableCell align={isRTL ? "right" : "left"}>
              <Skeleton variant="text" width="100%" />
            </TableCell>
            <TableCell align={isRTL ? "right" : "left"}>
              <Skeleton variant="text" width="100%" />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from({ length: 3 }).map((_, index) => (
            <TableRow key={index}>
              <TableCell align={isRTL ? "right" : "left"}>
                <Skeleton variant="text" width="60%" />
              </TableCell>
              <TableCell align={isRTL ? "right" : "left"}>
                <Skeleton variant="text" width="70%" />
              </TableCell>
              <TableCell align={isRTL ? "right" : "left"}>
                <Skeleton variant="rounded" width={70} height={24} />
              </TableCell>
              <TableCell align={isRTL ? "right" : "left"}>
                <Skeleton variant="text" width="80%" />
              </TableCell>
              <TableCell align={isRTL ? "right" : "left"}>
                <Skeleton variant="text" width="90%" />
              </TableCell>
              <TableCell align={isRTL ? "right" : "left"}>
                <Skeleton variant="rounded" width={80} height={30} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const ModelConfigSkeleton = () => (
  <Card sx={{ mt: 3 }}>
    <CardContent>
      <Skeleton variant="text" width="40%" height={32} />
      <Divider sx={{ mb: 2 }} />

      {Array.from({ length: 4 }).map((_, index) => (
        <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Skeleton variant="text" width="80%" height={20} />
          </Grid>
          <Grid item xs={6}>
            <Skeleton variant="text" width="40%" height={20} />
          </Grid>
        </Grid>
      ))}
    </CardContent>
  </Card>
);

// Tab panel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`deployment-tabpanel-${index}`}
      aria-labelledby={`deployment-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

// Add a function to mask API keys
const maskApiKey = (key) => {
  if (!key) return "••••••••••••••••••••••••••••••••";
  if (key.startsWith("omni_")) {
    // Show first 5 and last 4 characters
    return `${key.substring(0, 5)}••••••••••••••${key.substring(
      key.length - 4,
    )}`;
  }
  return "••••••••••••••••••••••••••••••••";
};

// Add function to truncate API keys for code examples
const truncateKeyForCodeExample = (key) => {
  if (!key || key === "YOUR_API_KEY") return "YOUR_API_KEY";

  // If key is longer than 20 characters, truncate it for display in code examples
  if (key.length > 20) {
    return key.substring(0, 10) + "..." + key.substring(key.length - 7);
  }
  return key;
};

const DeploymentDetail = () => {
  const { t, i18n: i18nInstance } = useTranslation();
  const isRTL = i18nInstance.dir() === "rtl";
  const locale = i18nInstance.resolvedLanguage || i18nInstance.language;
  const { id } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { mode } = useThemeMode();

  // eslint-disable-next-line no-unused-vars
  const [deploymentData, setDeploymentData] = useState(null);
  const [deployment, setDeployment] = useState(null);
  const [versions, setVersions] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timelineLoading, setTimelineLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [createKeyDialogOpen, setCreateKeyDialogOpen] = useState(false);
  const [newKeyDescription, setNewKeyDescription] = useState("");
  const [newKeyExpiry, setNewKeyExpiry] = useState("30");
  const [createKeyLoading, setCreateKeyLoading] = useState(false);
  const [deleteKeyDialogOpen, setDeleteKeyDialogOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [deleteKeyLoading, setDeleteKeyLoading] = useState(false);
  const [codeExampleTab, setCodeExampleTab] = useState(0);
  const [createVersionDialogOpen, setCreateVersionDialogOpen] = useState(false);
  const [createVersionLoading, setCreateVersionLoading] = useState(false);
  const [versionForm, setVersionForm] = useState({
    name: "",
    model_source: "hf",
    model_url: "",
    max_replicas: 1,
    is_llm: false,
    gpu_count: 1,
    memory_request: "16Gi",
    memory_limit: "16Gi",
    cpu_request: "1",
    cpu_limit: "2",
    deeplearning: {
      input_size: "1 3 224 224",
      latency_constraint: 100,
      throughput_constraint: 60,
      gpu_used_memory: 4000,
      max_batch_size: 8,
    },
    llm: {
      speculative_model: false,
    },
    aws_access_key_id: "",
    aws_secret_access_key: "",
    aws_region: "us-east-1",
    hf_token: "",
  });
  const [versionFormStep, setVersionFormStep] = useState(0);
  const [registerForRagDialogOpen, setRegisterForRagDialogOpen] =
    useState(false);
  const [llmConfig, setLlmConfig] = useState(null);
  const [llmConfigLoading, setLlmConfigLoading] = useState(false);
  const [expandedSteps, setExpandedSteps] = useState({});
  const [revealedKeys, setRevealedKeys] = useState({});
  const [keyLoading, setKeyLoading] = useState({});
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const menuOpen = Boolean(menuAnchorEl);
  const [scheduleUptimeDialogOpen, setScheduleUptimeDialogOpen] =
    useState(false);
  const [schedules, setSchedules] = useState([]);
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  const [editScheduleDialogOpen, setEditScheduleDialogOpen] = useState(false);
  const [deleteScheduleDialogOpen, setDeleteScheduleDialogOpen] =
    useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [deleteScheduleLoading, setDeleteScheduleLoading] = useState(false);
  const [updatingSchedules, setUpdatingSchedules] = useState({});

  // Memoize the active API keys to prevent unnecessary re-renders
  const activeApiKeys = useMemo(() => {
    return apiKeys.filter((key) => key.is_active);
  }, [apiKeys]);

  const getDisplayValue = useCallback(
    (value) => value || t("common.notAvailable"),
    [t],
  );

  const getModelSourceLabel = useCallback(
    (source) => {
      switch (source) {
        case "hf":
          return t("deployments.huggingFace");
        case "s3":
          return t("deployments.s3Bucket");
        case "http":
        case "https":
          return t("deployments.httpUrl");
        case "local":
          return t("predefinedDeployment.local");
        default:
          return source || t("common.unknown");
      }
    },
    [t],
  );

  const getModelTypeLabel = useCallback(
    (isLlm) =>
      isLlm === true ? t("deployments.llm") : t("deployments.deepLearning"),
    [t],
  );

  const getDeploymentStatusLabel = useCallback(
    (status) => {
      if (!status) {
        return t("common.unknown");
      }

      const statusKeyMap = {
        ACTIVE: "common.active",
        ARCHIVED: "deployments.archived",
        COMPLETED: "deploymentDetail.completed",
        DOWNLOADING: "deploymentDetail.downloading",
        RESUMED: "deploymentDetail.resumed",
        ERROR: "common.error",
        FAILED: "deployments.failed",
        INACTIVE: "common.inactive",
        LOADING: "common.loading",
        PAUSED: "deployments.paused",
        PENDING: "deployments.pending",
        READY: "deploymentDetail.ready",
        RUNNING: "deployments.running",
        STARTED: "deployments.started",
        STARTING: "deployments.starting",
        STOPPED: "deployments.stopped",
        SUCCESS: "deployments.success",
        WARNING: "deploymentDetail.warning",
      };
      const normalizedStatus = status.toUpperCase();

      return statusKeyMap[normalizedStatus]
        ? t(statusKeyMap[normalizedStatus])
        : status;
    },
    [t],
  );

  // Define checkModelRegistration before using it in useEffect
  const checkModelRegistration = useCallback(async () => {
    setLlmConfigLoading(true);
    try {
      const externalId = deployment?.model_id || id;
      const config = await llmApi.getLLMConfigByExternalId(externalId);
      setLlmConfig(config);
    } catch (error) {
      console.error("Error checking model registration:", error);
    } finally {
      setLlmConfigLoading(false);
    }
  }, [deployment, id]);

  useEffect(() => {
    // Reset loading states when ID changes
    setLoading(true);
    setTimelineLoading(true);
    setRevealedKeys({}); // Reset revealed keys when deployment ID changes
    setKeyLoading({}); // Reset key loading state

    // Load deployment data and timeline in parallel
    fetchDeploymentData();
    fetchTimeline();
    fetchSchedules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Check if this model is already registered for RAG
  useEffect(() => {
    if (deployment?.model_id) {
      checkModelRegistration();
    }
  }, [deployment, checkModelRegistration]);

  // Update this useEffect to initialize the first step as expanded when timeline loads
  useEffect(() => {
    if (timeline.length > 0) {
      // Group timeline events by step
      const groupedEvents = {};
      timeline.forEach((event) => {
        if (!groupedEvents[event.step]) {
          groupedEvents[event.step] = [];
        }
        groupedEvents[event.step].push(event);
      });

      // Use the order from the API response - get unique steps in the order they appear
      const orderedSteps = [];
      timeline.forEach((event) => {
        if (!orderedSteps.includes(event.step)) {
          orderedSteps.push(event.step);
        }
      });

      if (orderedSteps.length > 0) {
        // Find the current active step (in progress) or the latest completed step
        let stepToExpand = null;

        // First, look for a step that's currently in progress (has STARTED status but no SUCCESS)
        for (const step of orderedSteps) {
          const stepEvents = groupedEvents[step];
          const hasSuccess = stepEvents.some(
            (event) => event.status === "SUCCESS",
          );
          const hasStarted = stepEvents.some(
            (event) => event.status === "STARTED",
          );

          if (hasStarted && !hasSuccess) {
            stepToExpand = step;
            break; // Found an in-progress step, use this one
          }
        }

        // If no in-progress step found, use the latest completed step
        if (!stepToExpand) {
          // Find the last step that has a SUCCESS status
          for (let i = orderedSteps.length - 1; i >= 0; i--) {
            const step = orderedSteps[i];
            const stepEvents = groupedEvents[step];
            const hasSuccess = stepEvents.some(
              (event) => event.status === "SUCCESS",
            );

            if (hasSuccess) {
              stepToExpand = step;
              break;
            }
          }
        }

        // Fallback to the last step if nothing else found
        if (!stepToExpand) {
          stepToExpand = orderedSteps[orderedSteps.length - 1];
        }

        // Initialize with the determined step expanded
        const initialExpandedState = {};
        if (stepToExpand) {
          initialExpandedState[stepToExpand] = true;
        }
        setExpandedSteps(initialExpandedState);
      }
    }
  }, [timeline]);

  const fetchDeploymentData = async () => {
    setLoading(true);
    try {
      const response = await deploymentApi.getDeployment(id);

      // The API returns a list of deployment versions or a single deployment
      const deploymentVersions = Array.isArray(response.data)
        ? response.data
        : [response.data];

      if (deploymentVersions.length === 0) {
        toast.error(t("deploymentDetail.noDeploymentFoundWithId"));
        return;
      }

      // Store all deployment data
      setDeploymentData(deploymentVersions);

      // Set the current active deployment (first item is most recent version)
      const activeDeployment =
        deploymentVersions.find((d) => d.is_active) || deploymentVersions[0];
      setDeployment(activeDeployment);

      // Set versions
      setVersions(deploymentVersions);

      // Extract API keys if available
      if (
        activeDeployment.api_keys &&
        Array.isArray(activeDeployment.api_keys)
      ) {
        setApiKeys(activeDeployment.api_keys);
      }
    } catch (error) {
      console.error("Error fetching deployment:", error);
      toast.error(t("deploymentDetail.failedToLoadDeploymentDetails"));
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeline = async () => {
    setTimelineLoading(true);
    try {
      const timelineResponse = await deploymentApi.getDeploymentTimeline(id);
      setTimeline(timelineResponse.data || []);
    } catch (error) {
      console.error("Error fetching timeline:", error);
    } finally {
      setTimelineLoading(false);
    }
  };

  const fetchSchedules = async () => {
    setSchedulesLoading(true);
    try {
      const schedulesResponse = await scheduledUptimeApi.listSchedules(id);
      setSchedules(schedulesResponse.data || []);
    } catch (error) {
      console.error("Error fetching schedules:", error);
      setSchedules([]);
    } finally {
      setSchedulesLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    try {
      // First check if this model is registered for RAG and delete it if needed
      if (llmConfig && llmConfig.id) {
        try {
          await llmApi.deleteLLMConfig(llmConfig.id);
        } catch (ragError) {
          console.error("Error unregistering model from RAG:", ragError);
          // Continue with deployment deletion even if RAG unregistration fails
        }
      }

      // Proceed with normal deployment deletion
      await deploymentApi.deleteDeployment(id);
      toast.success(t("deployments.deploymentDeletedSuccess"));
      navigate("/deployments");
    } catch (error) {
      console.error("Error deleting deployment:", error);
      toast.error(t("deployments.failedToDeleteDeployment"));
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleCreateKeyCancel = () => {
    setCreateKeyDialogOpen(false);
    setNewKeyDescription("");
    setNewKeyExpiry("30");
  };

  const handleCreateKeyConfirm = async () => {
    setCreateKeyLoading(true);
    try {
      // Prepare parameters
      const description = newKeyDescription.trim() || null;
      const expiresInDays = newKeyExpiry ? parseInt(newKeyExpiry) : null;

      // Call the API with the required parameters
      const response = await apiKeysApi.createApiKey(
        id,
        currentUser.uid,
        description,
        expiresInDays,
      );

      // Add the new key to the list and reveal it
      const newKey = response.data;
      setApiKeys([...apiKeys, newKey]);

      // Automatically reveal the new key
      setRevealedKeys((prev) => ({
        ...prev,
        [newKey.id]: newKey.key,
      }));

      // Show success message and close dialog
      toast.success(t("deploymentDetail.apiKeyCreatedSuccess"));
      setCreateKeyDialogOpen(false);

      // Reset form
      setNewKeyDescription("");
      setNewKeyExpiry("30");
    } catch (error) {
      console.error("Error creating API key:", error);
      toast.error(t("deploymentDetail.failedToCreateApiKey"));
    } finally {
      setCreateKeyLoading(false);
    }
  };

  const handleDeleteKeyCancel = () => {
    setDeleteKeyDialogOpen(false);
    setSelectedKey(null);
  };

  const handleDeleteKeyConfirm = async () => {
    if (!selectedKey) return;

    setDeleteKeyLoading(true);
    try {
      await apiKeysApi.deleteApiKey(id, selectedKey.id, currentUser.uid);

      // Remove the deleted key from the list
      setApiKeys(apiKeys.filter((key) => key.id !== selectedKey.id));

      // Also remove from revealed keys if it was revealed
      if (revealedKeys[selectedKey.id]) {
        setRevealedKeys((prev) => {
          const updated = { ...prev };
          delete updated[selectedKey.id];
          return updated;
        });
      }

      toast.success(t("deploymentDetail.apiKeyDeletedSuccess"));
      setDeleteKeyDialogOpen(false);
      setSelectedKey(null);
    } catch (error) {
      console.error("Error deleting API key:", error);
      toast.error(t("deploymentDetail.failedToDeleteApiKey"));
    } finally {
      setDeleteKeyLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return t("common.notAvailable");
    return new Date(dateString).toLocaleString(locale);
  };

  // Get status color
  const getStatusColor = (status) => {
    if (!status) return "default";

    const statusUpper = status.toUpperCase();
    switch (statusUpper) {
      case "RUNNING":
        return "success";
      case "PENDING":
      case "STARTING":
      case "DOWNLOADING":
        return "warning";
      case "FAILED":
        return "error";
      case "STOPPED":
        return "default";
      default:
        return "default";
    }
  };

  // Add a function to handle revealing API keys
  const handleRevealKey = async (apiKey) => {
    if (revealedKeys[apiKey.id]) {
      // If already revealed, hide it
      setRevealedKeys((prev) => ({
        ...prev,
        [apiKey.id]: false,
      }));
      return;
    }

    // Set loading state for this key
    setKeyLoading((prev) => ({
      ...prev,
      [apiKey.id]: true,
    }));

    try {
      // Check if we already have the full key (for newly created keys)
      if (apiKey.key && apiKey.key.startsWith("omni_")) {
        // If we already have the full key, just reveal it
        setRevealedKeys((prev) => ({
          ...prev,
          [apiKey.id]: apiKey.key,
        }));
      } else {
        // Otherwise, fetch the decrypted key from the backend
        const response = await apiKeysApi.getApiKey(id, apiKey.id);

        // Update the revealed keys state
        setRevealedKeys((prev) => ({
          ...prev,
          [apiKey.id]: response.data.key,
        }));
      }
    } catch (error) {
      console.error("Error fetching API key:", error);
      toast.error(t("deploymentDetail.failedToRevealApiKey"));
    } finally {
      // Clear loading state
      setKeyLoading((prev) => ({
        ...prev,
        [apiKey.id]: false,
      }));
    }
  };

  // eslint-disable-next-line no-unused-vars
  const getApiUsageCode = (apiKey) => {
    const fullKey = revealedKeys[apiKey.id] || apiKey.key || "YOUR_API_KEY";
    const key = sanitizeForCodeString(truncateKeyForCodeExample(fullKey));
    const endpointUrl = sanitizeForCodeString(getModelEndpointUrl());
    return {
      python: `import requests

api_url = "${endpointUrl}"
headers = {"OMNIMIZE_APIKEY": "${key}"}

# For image input (adjust according to your model's requirements)
files = {'file': open('image.jpg', 'rb')}
response = requests.post(api_url, headers=headers, files=files)

print(response.json())`,
      javascript: `// Using fetch API
fetch("${endpointUrl}", {
  method: "POST",
  headers: {
    "OMNIMIZE_APIKEY": "${key}"
  },
  body: formData // FormData containing your input files
})
.then(response => response.json())
.catch(error => console.error('Error:', error));`,
    };
  };

  // Add a function to format the expiry date
  const formatExpiryDate = (expiresAt) => {
    if (!expiresAt) return t("common.never");

    const expiryDate = new Date(expiresAt);
    const now = new Date();

    // Calculate days remaining
    const diffTime = expiryDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) {
      return t("deploymentDetail.expired");
    } else if (diffDays === 1) {
      return t("deploymentDetail.expiresTomorrow");
    } else {
      return t("deploymentDetail.expiresInDaysCount", { days: diffDays });
    }
  };

  // Update the getModelEndpointUrl function to use the API domain
  const getModelEndpointUrl = () => {
    if (!deployment) return "";

    // If endpoint_url is provided, use it directly
    if (deployment.endpoint_url) {
      return deployment.endpoint_url;
    }

    // Fallback to manual construction if endpoint_url is not available
    const apiBaseUrl =
      window.env.REACT_APP_API_BASE_URL ||
      "https://api.omnimize.poc.omniops.sa";

    // Determine the endpoint path based on model type
    const endpointPath =
      deployment.args?.is_llm === true
        ? `/vllm/${deployment.model_id}`
        : `/dl/${deployment.model_id}`;

    return `${apiBaseUrl}${endpointPath}`;
  };

  // Update the getOpenAICodeExample function
  // Sanitize strings to prevent code injection
  const sanitizeForCodeString = (str) => {
    if (!str) return "";
    return str.replace(/["\\\n\r\t]/g, (match) => {
      switch (match) {
        case '"':
          return '\\"';
        case "\\":
          return "\\\\";
        case "\n":
          return "\\n";
        case "\r":
          return "\\r";
        case "\t":
          return "\\t";
        default:
          return match;
      }
    });
  };

  const getOpenAICodeExample = (apiKey) => {
    const fullKey = apiKey
      ? revealedKeys[apiKey.id] || apiKey.key || "YOUR_API_KEY"
      : "YOUR_API_KEY";
    const key = sanitizeForCodeString(truncateKeyForCodeExample(fullKey));
    const baseUrl = sanitizeForCodeString(
      deployment.endpoint_url || getModelEndpointUrl(),
    ); // Ensure getModelEndpointUrl() is appropriate for all types
    const modelName = sanitizeForCodeString(deployment?.name || "default");

    if (deployment?.args?.llm?.is_embedding) {
      return `import os
import httpx
from openai import OpenAI

# Set up the API key (best to use environment variable)
api_key = "${key}"  # or use: os.getenv("OMNIMIZE_APIKEY")

# Configure custom HTTP client with the correct header
http_client = httpx.Client(
    headers={"OMNIMIZE_APIKEY": api_key}
)

# Initialize the client with your custom HTTP client and base URL
client = OpenAI(
    base_url="${baseUrl.replace("/chat/completions", "")}",
    http_client=http_client
)

# Create an embedding
response = client.embeddings.create(
    model="${modelName}", # Use the model name from deployment
    input="Your text string goes here",
    # encoding_format="float" # Optional: specify encoding format
)

# Print the embedding
print(response.data[0].embedding)`;
    } else if (deployment?.args?.llm?.is_multimodal) {
      return `import os
import httpx
import base64
import requests  # Required for fetching image if it's a URL
from openai import OpenAI

# Set up the API key (best to use environment variable)
api_key = "${key}"  # or use: os.getenv("OMNIMIZE_APIKEY")

# Function to encode image to base64
def encode_image_to_base64(image_path_or_url):
    if image_path_or_url.startswith('http://') or image_path_or_url.startswith('https://'):
        response = requests.get(image_path_or_url)
        response.raise_for_status()
        return base64.b64encode(response.content).decode('utf-8')
    else:
        with open(image_path_or_url, "rb") as image_file:
            return base64.b64encode(image_file.read()).decode('utf-8')

# Configure custom HTTP client with the correct header
http_client = httpx.Client(
    headers={"OMNIMIZE_APIKEY": api_key}
)

# Initialize the client with your custom HTTP client and base URL
client = OpenAI(
    base_url="${baseUrl}",
    http_client=http_client
)

# Example with a local image file or a public image URL
# Replace with your actual image path or URL
image_source = "path/to/your/image.jpg"
# or image_source = "https://example.com/image.jpg"

try:
    base64_image = encode_image_to_base64(image_source)

    # Create a chat completion with an image
    response = client.chat.completions.create(
        model="${modelName}", # Use the model name from deployment
        messages=[
            {
                "role": "user",
                "content": [
                    {"type": "text", "text": "What's in this image?"},
                    {
                        "type": "image_url",
                        "image_url": {
                            "url": f"data:image/jpeg;base64,{base64_image}"
                        }
                    }
                ]
            }
        ],
        temperature=0.7,
        max_tokens=500
    )

    # Print the response
    print(response.choices[0].message.content)

except Exception as e:
    print(f"An error occurred: {e}")
    print("Please ensure the image path is correct or the URL is accessible and points to a valid image.")`;
    } else if (deployment?.args?.is_llm) {
      // Default to existing chat completion for LLMs
      return `import os
import httpx
from openai import OpenAI

# Set up the API key (best to use environment variable)
api_key = "${key}"  # or use: os.getenv("OMNIMIZE_APIKEY")

# Configure custom HTTP client with the correct header
http_client = httpx.Client(
    headers={"OMNIMIZE_APIKEY": api_key}
)

# Initialize the client with your custom HTTP client and base URL
client = OpenAI(
    base_url="${baseUrl}",
    http_client=http_client
)

# Create a chat completion
response = client.chat.completions.create(
    model="${modelName}", # Use the model name from deployment
    messages=[
        {"role": "system", "content": "You are a helpful assistant."},
        {"role": "user", "content": "Tell me about artificial intelligence."}
    ],
    temperature=0.7,
    max_tokens=500
)

# Print the response
print(response.choices[0].message.content)`;
    }
    // Fallback or for non-LLM models, decide what to show or return empty/message
    return "# Code example not available for this model type";
  };

  // Update the getCurlCodeExample function
  const getCurlCodeExample = (apiKey) => {
    const fullKey = apiKey
      ? revealedKeys[apiKey.id] || apiKey.key || "YOUR_API_KEY"
      : "YOUR_API_KEY";
    const key = sanitizeForCodeString(truncateKeyForCodeExample(fullKey));
    const baseUrl = sanitizeForCodeString(
      deployment.endpoint_url || getModelEndpointUrl(),
    );
    const modelName = sanitizeForCodeString(deployment?.name || "default");

    if (deployment?.args?.llm?.is_embedding) {
      // Ensure the embedding endpoint is correctly formed.
      // This might need adjustment based on your actual API structure.
      const embeddingBaseUrl = baseUrl.replace(
        "/v1/chat/completions",
        "/v1/embeddings",
      );
      return `curl -X POST "${embeddingBaseUrl}" \\
    -H "Content-Type: application/json" \\
    -H "OMNIMIZE_APIKEY: ${key}" \\
    -d '{
      "model": "${modelName}",
      "input": "Your text string goes here"
    }'`;
    } else if (deployment?.args?.llm?.is_multimodal) {
      // For cURL, sending an image typically involves multipart/form-data
      // or a base64 encoded string within the JSON payload if the API supports it.
      // The OpenAI API (and compatible ones) for multimodal expect a base64 string.
      // This example assumes the API expects a base64 encoded image in the message content.
      return `curl -X POST "${baseUrl}" \\
    -H "Content-Type: application/json" \\
    -H "OMNIMIZE_APIKEY: ${key}" \\
    -d '{
      "model": "${modelName}",
      "messages": [
        {
          "role": "user",
          "content": [
            {
              "type": "text",
              "text": "What's in this image?"
            },
            {
              "type": "image_url",
              "image_url": {
                "url": "data:image/jpeg;base64,YOUR_BASE64_ENCODED_IMAGE_HERE"
              }
            }
          ]
        }
      ],
      "temperature": 0.7,
      "max_tokens": 500
    }'

# Note: Replace YOUR_BASE64_ENCODED_IMAGE_HERE with the actual base64 encoded image string.
# You can use online tools or a script to convert your image to base64.
# Example (bash using base64 utility):
# IMAGE_BASE64=$(base64 -w 0 your_image.jpg)
# Then replace in the curl command.`;
    } else if (deployment?.args?.is_llm) {
      // Default to existing chat completion for LLMs
      return `curl -X POST "${baseUrl}" \\
    -H "Content-Type: application/json" \\
    -H "OMNIMIZE_APIKEY: ${key}" \\
    -d '{
      "model": "${modelName}",
      "messages": [
        {"role": "system", "content": "You are a helpful assistant."},
        {"role": "user", "content": "Tell me about artificial intelligence."}
      ],
      "temperature": 0.7,
      "max_tokens": 500
    }'`;
    }
    // Fallback or for non-LLM models
    return "# cURL example not available for this model type";
  };

  const handleActivateVersion = async (version) => {
    try {
      await deploymentApi.activateVersion(id, version);
      toast.success(t("deploymentDetail.versionActivatedSuccess", { version }));
      // After activation, fetch the updated deployment data
      fetchDeploymentData();
    } catch (error) {
      console.error("Error activating version:", error);
      toast.error(t("deploymentDetail.versionActivationFailed", { version }));
    }
  };

  const handleOpenVersionDialog = () => {
    // Initialize form with current deployment data
    if (deployment) {
      setVersionForm({
        name: deployment.name,
        model_source: deployment.model_source || "hf",
        model_url: deployment.model_url || "",
        max_replicas: deployment.max_replicas || 1,
        is_llm: deployment.args?.is_llm === true,
        gpu_count: deployment.resources?.gpu_count || 1,
        memory_request: deployment.resources?.memory_request || "16Gi",
        memory_limit: deployment.resources?.memory_limit || "16Gi",
        cpu_request: deployment.resources?.cpu_request || "1",
        cpu_limit: deployment.resources?.cpu_limit || "2",
        deeplearning: {
          input_size:
            deployment.args?.deeplearning?.input_size || "1 3 224 224",
          latency_constraint:
            deployment.args?.deeplearning?.latency_constraint || 100,
          throughput_constraint:
            deployment.args?.deeplearning?.throughput_constraint || 60,
          gpu_used_memory:
            deployment.args?.deeplearning?.gpu_used_memory || 4000,
          max_batch_size: deployment.args?.deeplearning?.max_batch_size || 8,
        },
        llm: {
          speculative_model: deployment.args?.llm?.speculative_model || false,
        },
        aws_access_key_id: "",
        aws_secret_access_key: "",
        aws_region: deployment.aws_region || "us-east-1",
        hf_token: "",
      });
    }

    setVersionFormStep(0);
    setCreateVersionDialogOpen(true);
  };

  const handleVersionFormChange = (field, value) => {
    setVersionForm((prev) => {
      // Handle nested fields
      if (field.includes(".")) {
        const [parent, child] = field.split(".");
        return {
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: value,
          },
        };
      }

      return {
        ...prev,
        [field]: value,
      };
    });
  };

  const handleCreateVersion = async () => {
    setCreateVersionLoading(true);
    try {
      await deploymentApi.createNewVersion(id, versionForm);
      toast.success(t("deploymentDetail.versionCreatedSuccess"));
      fetchDeploymentData(); // Fetch updated deployment data including new version
      setCreateVersionDialogOpen(false);
    } catch (error) {
      console.error("Error creating new version:", error);
      toast.error(t("deploymentDetail.versionCreateFailed"));
    } finally {
      setCreateVersionLoading(false);
    }
  };

  const handleViewUsage = () => {
    navigate(`/usage?deploymentId=${id}`);
  };

  const handleRegisterForRagClick = () => {
    setRegisterForRagDialogOpen(true);
  };

  const handleUnregisterForRagClick = async () => {
    if (!llmConfig || !llmConfig.id) {
      toast.error(t("deploymentDetail.noRegisteredModelFound"));
      return;
    }

    try {
      await llmApi.deleteLLMConfig(llmConfig.id);
      toast.success(t("deploymentDetail.modelUnregisteredSuccess"));
      // Refresh the registration status
      setLlmConfig(null);
    } catch (error) {
      console.error("Error unregistering model:", error);
      toast.error(
        t("deploymentDetail.failedToUnregisterModel", {
          message: error.message,
        }),
      );
    }
  };

  // Add a function to toggle step expansion
  const toggleStepExpanded = (step) => {
    setExpandedSteps((prev) => ({
      ...prev,
      [step]: !prev[step],
    }));
  };

  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleMenuAction = (action) => {
    handleMenuClose();

    switch (action) {
      case "refresh":
        // Refresh all data
        fetchDeploymentData();
        fetchTimeline();
        fetchSchedules();
        break;
      case "register-rag":
        handleRegisterForRagClick();
        break;
      case "unregister-rag":
        handleUnregisterForRagClick();
        break;
      case "schedule-uptime":
        setScheduleUptimeDialogOpen(true);
        break;
      case "view-usage":
        handleViewUsage();
        break;
      case "delete":
        handleDeleteClick();
        break;
      default:
        break;
    }
  };

  const handleEditSchedule = (schedule) => {
    setSelectedSchedule(schedule);
    setEditScheduleDialogOpen(true);
  };

  const handleDeleteSchedule = (schedule) => {
    setSelectedSchedule(schedule);
    setDeleteScheduleDialogOpen(true);
  };

  const handleDeleteScheduleConfirm = async () => {
    if (!selectedSchedule) return;

    setDeleteScheduleLoading(true);
    try {
      await scheduledUptimeApi.deleteSchedule(selectedSchedule.id);
      toast.success(t("deploymentDetail.scheduleDeletedSuccess"));
      fetchSchedules(); // Refresh the schedules list
      setDeleteScheduleDialogOpen(false);
      setSelectedSchedule(null);
    } catch (error) {
      console.error("Error deleting schedule:", error);
      toast.error(t("deploymentDetail.failedToDeleteSchedule"));
    } finally {
      setDeleteScheduleLoading(false);
    }
  };

  const handleDeleteScheduleCancel = () => {
    setDeleteScheduleDialogOpen(false);
    setSelectedSchedule(null);
  };

  const handleToggleScheduleActive = async (schedule) => {
    const scheduleId = schedule.id;

    // Set loading state for this specific schedule
    setUpdatingSchedules((prev) => ({
      ...prev,
      [scheduleId]: true,
    }));

    try {
      // Call the updateSchedule API to toggle the is_active status
      await scheduledUptimeApi.updateSchedule(scheduleId, {
        ...schedule,
        is_active: !schedule.is_active,
      });

      // Update the local schedules state
      setSchedules((prevSchedules) =>
        prevSchedules.map((s) =>
          s.id === scheduleId ? { ...s, is_active: !s.is_active } : s,
        ),
      );

      toast.success(
        schedule.is_active
          ? t("deploymentDetail.scheduleDeactivatedSuccess")
          : t("deploymentDetail.scheduleActivatedSuccess"),
      );
    } catch (error) {
      console.error("Error updating schedule:", error);
      toast.error(t("deploymentDetail.failedToUpdateScheduleStatus"));
    } finally {
      // Clear loading state
      setUpdatingSchedules((prev) => {
        const updated = { ...prev };
        delete updated[scheduleId];
        return updated;
      });
    }
  };

  const formatScheduleType = (scheduleType) => {
    switch (scheduleType) {
      case "daily":
        return t("schedule.daily");
      case "weekdays":
        return t("schedule.weekdaysOnly");
      case "weekly":
        return t("schedule.weekly");
      case "custom":
        return t("schedule.customDays");
      default:
        return scheduleType;
    }
  };

  const formatDaysOfWeek = (daysArray) => {
    if (!daysArray || daysArray.length === 0) return t("common.notAvailable");
    return daysArray
      .map((dayIndex) => {
        if (typeof dayIndex !== "number") {
          return dayIndex;
        }

        const date = new Date(Date.UTC(2024, 0, 1 + dayIndex));
        return new Intl.DateTimeFormat(locale, { weekday: "short" }).format(
          date,
        );
      })
      .join(", ");
  };

  return (
    <DashboardLayout title={deployment?.name || t("deploymentDetail.title")}>
      {/* Page Header - Show skeleton during loading */}
      {loading ? (
        <DeploymentHeaderSkeleton />
      ) : deployment ? (
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs>
              <Typography variant="h4" component="h1">
                {deployment.name}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                <Chip
                  label={getDeploymentStatusLabel(deployment.status)}
                  color={getStatusColor(deployment.status)}
                  size="small"
                  sx={{ marginInlineEnd: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {t("deploymentDetail.deploymentId")}:{" "}
                  {deployment.model_id || id}
                </Typography>
              </Box>
            </Grid>
            <Grid item>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  "& > button": {
                    flex: { xs: "1 1 auto", sm: "0 1 auto" },
                    minWidth: { xs: "100%", sm: "auto" },
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<MoreVertIcon />}
                  onClick={handleMenuOpen}
                  endIcon={<ExpandMoreIcon />}
                >
                  {t("common.actions")}
                </Button>

                <Menu
                  anchorEl={menuAnchorEl}
                  open={menuOpen}
                  onClose={handleMenuClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                >
                  <MenuItem onClick={() => handleMenuAction("refresh")}>
                    <RefreshIcon sx={{ marginInlineEnd: 1 }} />
                    {t("common.refresh")}
                  </MenuItem>

                  {llmConfigLoading ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} sx={{ marginInlineEnd: 1 }} />
                      {t("deploymentDetail.checking")}
                    </MenuItem>
                  ) : llmConfig ? (
                    <MenuItem
                      onClick={() => handleMenuAction("unregister-rag")}
                    >
                      <PsychologyIcon sx={{ marginInlineEnd: 1 }} />
                      {t("deploymentDetail.unregisterFromRag")}
                    </MenuItem>
                  ) : (
                    <MenuItem onClick={() => handleMenuAction("register-rag")}>
                      <PsychologyIcon sx={{ marginInlineEnd: 1 }} />
                      {t("deploymentDetail.registerForRag")}
                    </MenuItem>
                  )}

                  <MenuItem onClick={() => handleMenuAction("schedule-uptime")}>
                    <ScheduleIcon sx={{ marginInlineEnd: 1 }} />
                    {t("deploymentDetail.scheduleUptime")}
                  </MenuItem>

                  <MenuItem onClick={() => handleMenuAction("view-usage")}>
                    <AttachMoneyIcon sx={{ marginInlineEnd: 1 }} />
                    {t("deploymentDetail.viewUsage")}
                  </MenuItem>

                  <Divider />

                  <MenuItem
                    onClick={() => handleMenuAction("delete")}
                    sx={{ color: "error.main" }}
                  >
                    <DeleteIcon sx={{ marginInlineEnd: 1 }} color="error" />
                    {t("common.delete")}
                  </MenuItem>
                </Menu>
              </Box>
            </Grid>
          </Grid>
        </Box>
      ) : (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" color="error">
            {t("deploymentDetail.deploymentNotFound")}
          </Typography>
        </Box>
      )}

      {/* Tabs are always shown, regardless of loading state */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label={t("deploymentDetail.deploymentTabsAriaLabel")}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            mb: 2,
            "& button": {
              fontSize: "14px",
            },
          }}
        >
          <Tab icon={<SettingsIcon />} label={t("deploymentDetail.overview")} />
          <Tab icon={<KeyIcon />} label={t("deploymentDetail.apiKeys")} />
          <Tab icon={<TimelineIcon />} label={t("deploymentDetail.timeline")} />
          {/* <Tab icon={<HistoryIcon />} label="Versions" /> */}
          <Tab
            icon={<ScheduleIcon />}
            label={`${t("deploymentDetail.uptimeSchedules")} (${schedules.length})`}
          />
        </Tabs>

        {/* Overview Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              {loading ? (
                <ModelInfoSkeleton />
              ) : deployment ? (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {t("deployments.modelInformation")}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          {t("deploymentDetail.endpointUrl")}
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Typography
                            variant="body2"
                            sx={{ wordBreak: "break-all" }}
                          >
                            {getModelEndpointUrl()}
                          </Typography>
                          <Tooltip
                            title={t("deploymentDetail.copyEndpointUrl")}
                          >
                            <IconButton
                              size="small"
                              onClick={() => {
                                navigator.clipboard.writeText(
                                  getModelEndpointUrl(),
                                );
                                toast.success(t("common.copiedToClipboard"));
                              }}
                            >
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Grid>

                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          {t("deployments.modelSource")}
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2">
                          {getModelSourceLabel(deployment?.model_source)}
                        </Typography>
                      </Grid>

                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          {t("deployments.modelUrl")}
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography
                          variant="body2"
                          sx={{ wordBreak: "break-all" }}
                        >
                          {getDisplayValue(deployment?.model_url)}
                        </Typography>
                      </Grid>

                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          {t("deployments.modelType")}
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2">
                          {getModelTypeLabel(deployment?.args?.is_llm)}
                        </Typography>
                      </Grid>

                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          {t("common.status")}
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Chip
                          label={getDeploymentStatusLabel(deployment?.status)}
                          color={getStatusColor(deployment?.status)}
                          size="small"
                        />
                        {deployment?.error && (
                          <Typography
                            variant="body2"
                            color="error.main"
                            sx={{ mt: 1 }}
                          >
                            {t("common.error")}: {deployment.error}
                          </Typography>
                        )}
                      </Grid>

                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          {t("deploymentDetail.currentStep")}
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2">
                          {getDisplayValue(
                            deployment?.current_step
                              ? formatStepName(deployment.current_step, t)
                              : null,
                          )}
                        </Typography>
                      </Grid>

                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          {t("common.created")}
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2">
                          {formatDate(deployment?.created_at)}
                        </Typography>
                      </Grid>

                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          {t("common.version")}
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2">
                          {getDisplayValue(deployment?.version)}
                        </Typography>
                      </Grid>

                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          {t("deploymentDetail.modelId")}
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography
                          variant="body2"
                          sx={{ wordBreak: "break-all" }}
                        >
                          {deployment.model_id || id}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ) : null}
            </Grid>

            <Grid item xs={12} md={6}>
              {loading ? (
                <>
                  <ResourcesSkeleton />
                  <ModelConfigSkeleton />
                </>
              ) : deployment ? (
                <>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {t("deploymentDetail.resourceConfiguration")}
                      </Typography>
                      <Divider sx={{ mb: 2 }} />

                      <Grid container spacing={2}>
                        <Grid item xs={4}>
                          <Typography variant="body2" color="text.secondary">
                            {t("deployments.gpuCount")}
                          </Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Typography variant="body2">
                            {deployment.resources?.gpu_count || 0}
                          </Typography>
                        </Grid>

                        <Grid item xs={4}>
                          <Typography variant="body2" color="text.secondary">
                            {t("predefinedModels.memory")}
                          </Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Typography variant="body2">
                            {getDisplayValue(
                              deployment.resources?.memory_request,
                            )}{" "}
                            ({t("deploymentDetail.request")}) /{" "}
                            {getDisplayValue(
                              deployment.resources?.memory_limit,
                            )}{" "}
                            ({t("deploymentDetail.limit")})
                          </Typography>
                        </Grid>

                        <Grid item xs={4}>
                          <Typography variant="body2" color="text.secondary">
                            {t("deploymentDetail.cpu")}
                          </Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Typography variant="body2">
                            {getDisplayValue(deployment.resources?.cpu_request)}{" "}
                            ({t("deploymentDetail.request")}) /{" "}
                            {getDisplayValue(deployment.resources?.cpu_limit)} (
                            {t("deploymentDetail.limit")})
                          </Typography>
                        </Grid>

                        <Grid item xs={4}>
                          <Typography variant="body2" color="text.secondary">
                            {t("deploymentDetail.maxReplicas")}
                          </Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <Typography variant="body2">
                            {deployment.max_replicas || 1}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* Model-specific configuration */}
                  <Card sx={{ mt: 3 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {deployment.args?.is_llm
                          ? t("deploymentDetail.llmConfiguration")
                          : t("deploymentDetail.deepLearningConfiguration")}
                      </Typography>
                      <Divider sx={{ mb: 2 }} />

                      {deployment.args?.is_llm ? (
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              {t("deploymentDetail.useSpeculativeDecoding")}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2">
                              {deployment.args?.llm?.speculative_model
                                ? t("common.yes")
                                : t("common.no")}
                            </Typography>
                          </Grid>
                        </Grid>
                      ) : (
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              {t("deploymentDetail.inputSize")}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2">
                              {deployment.args?.deeplearning?.input_size ||
                                t("common.notAvailable")}
                            </Typography>
                          </Grid>

                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              {t("deploymentDetail.latencyConstraint")}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2">
                              {deployment.args?.deeplearning
                                ?.latency_constraint || 0}
                            </Typography>
                          </Grid>

                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              {t("deploymentDetail.throughputConstraint")}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2">
                              {deployment.args?.deeplearning
                                ?.throughput_constraint || 0}
                            </Typography>
                          </Grid>

                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              {t("deploymentDetail.gpuMemoryUsage")}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2">
                              {deployment.args?.deeplearning?.gpu_used_memory ||
                                0}
                            </Typography>
                          </Grid>

                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              {t("deploymentDetail.maxBatchSize")}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2">
                              {deployment.args?.deeplearning?.max_batch_size ||
                                0}
                            </Typography>
                          </Grid>
                        </Grid>
                      )}
                    </CardContent>
                  </Card>
                </>
              ) : null}
            </Grid>

            {/* LLM Code Examples Card */}
            {loading
              ? deployment?.args?.is_llm && (
                  <Grid item xs={12} sx={{ mt: 3 }}>
                    <ApiUsageSkeleton />
                  </Grid>
                )
              : deployment?.args?.is_llm && (
                  <Grid item xs={12} sx={{ mt: 3 }}>
                    <Card>
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 2,
                          }}
                        >
                          <Typography variant="h6">
                            {t("deploymentDetail.llmUsageExamples")}
                          </Typography>
                          {(() => {
                            // Find the first active key
                            const firstActiveKey = apiKeys.find(
                              (key) => key.is_active,
                            );

                            if (
                              firstActiveKey &&
                              !revealedKeys[firstActiveKey.id]
                            ) {
                              return (
                                <Button
                                  size="small"
                                  startIcon={<VisibilityIcon />}
                                  onClick={() =>
                                    handleRevealKey(firstActiveKey)
                                  }
                                  disabled={keyLoading[firstActiveKey.id]}
                                >
                                  {keyLoading[firstActiveKey.id]
                                    ? t("common.loading")
                                    : t(
                                        "deploymentDetail.revealKeyForExamples",
                                      )}
                                </Button>
                              );
                            }
                            return null;
                          })()}
                        </Box>
                        <Divider sx={{ mb: 2 }} />

                        <Box
                          sx={{
                            borderBottom: 1,
                            borderColor: "divider",
                            mb: 2,
                          }}
                        >
                          <Tabs
                            value={codeExampleTab}
                            onChange={(e, newValue) =>
                              setCodeExampleTab(newValue)
                            }
                          >
                            <Tab label={t("deploymentDetail.pythonOpenai")} />
                            <Tab label={t("deploymentDetail.curl")} />
                          </Tabs>
                        </Box>

                        {(() => {
                          // Find the first active key for examples
                          const firstActiveKey = apiKeys.find(
                            (key) => key.is_active,
                          );

                          return (
                            <>
                              {codeExampleTab === 0 && (
                                <Box sx={{ direction: "ltr" }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "flex-end",
                                      mb: 1,
                                    }}
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        navigator.clipboard.writeText(
                                          getOpenAICodeExample(
                                            firstActiveKey || null,
                                          ),
                                        );
                                        toast.success(
                                          t("deploymentDetail.codeCopied"),
                                        );
                                      }}
                                    >
                                      <CopyIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                  <SyntaxHighlighter
                                    language="python"
                                    style={
                                      mode === "dark"
                                        ? atomOneDark
                                        : atomOneLight
                                    }
                                    customStyle={{ borderRadius: "8px" }}
                                  >
                                    {getOpenAICodeExample(
                                      firstActiveKey || null,
                                    )}
                                  </SyntaxHighlighter>
                                </Box>
                              )}

                              {codeExampleTab === 1 && (
                                <Box sx={{ direction: "ltr" }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "flex-end",
                                      mb: 1,
                                    }}
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        navigator.clipboard.writeText(
                                          getCurlCodeExample(
                                            firstActiveKey || null,
                                          ),
                                        );
                                        toast.success(
                                          t("deploymentDetail.codeCopied"),
                                        );
                                      }}
                                    >
                                      <CopyIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                  <SyntaxHighlighter
                                    language="bash"
                                    style={
                                      mode === "dark"
                                        ? atomOneDark
                                        : atomOneLight
                                    }
                                    customStyle={{ borderRadius: "8px" }}
                                  >
                                    {getCurlCodeExample(firstActiveKey || null)}
                                  </SyntaxHighlighter>
                                </Box>
                              )}
                            </>
                          );
                        })()}
                      </CardContent>
                    </Card>
                  </Grid>
                )}
          </Grid>
        </TabPanel>

        {/* API Keys Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box
            sx={{
              mb: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">
              {t("deploymentDetail.apiKeys")}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateKeyDialogOpen(true)}
            >
              {t("deploymentDetail.createNewApiKey")}
            </Button>
          </Box>

          {loading ? (
            <ApiKeysTableSkeleton />
          ) : apiKeys.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell align={isRTL ? "right" : "left"}>
                      {t("deploymentDetail.description")}
                    </TableCell>
                    <TableCell align={isRTL ? "right" : "left"}>
                      {t("deploymentDetail.key")}
                    </TableCell>
                    <TableCell align={isRTL ? "right" : "left"}>
                      {t("common.created")}
                    </TableCell>
                    <TableCell align={isRTL ? "right" : "left"}>
                      {t("deploymentDetail.expiration")}
                    </TableCell>
                    <TableCell align={isRTL ? "right" : "left"}>
                      {t("common.status")}
                    </TableCell>
                    <TableCell align={isRTL ? "right" : "left"}>
                      {t("common.actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {apiKeys.map((apiKey) => (
                    <TableRow
                      key={apiKey.id}
                      sx={{
                        opacity: apiKey.is_active ? 1 : 0.5,
                        backgroundColor: apiKey.is_active
                          ? "inherit"
                          : "rgba(0, 0, 0, 0.04)",
                      }}
                    >
                      <TableCell align={isRTL ? "right" : "left"}>
                        {apiKey.description ||
                          t("deploymentDetail.noDescription")}
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: "200px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontFamily: "monospace",
                            }}
                          >
                            {revealedKeys[apiKey.id]
                              ? revealedKeys[apiKey.id]
                              : maskApiKey(apiKey.key)}
                          </Typography>
                          {apiKey.is_active && (
                            <>
                              <Tooltip
                                title={
                                  revealedKeys[apiKey.id]
                                    ? t("deploymentDetail.hideApiKey")
                                    : t("deploymentDetail.revealApiKey")
                                }
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => handleRevealKey(apiKey)}
                                  disabled={keyLoading[apiKey.id]}
                                >
                                  {keyLoading[apiKey.id] ? (
                                    <CircularProgress size={16} />
                                  ) : revealedKeys[apiKey.id] ? (
                                    <VisibilityOffIcon fontSize="small" />
                                  ) : (
                                    <VisibilityIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                              {revealedKeys[apiKey.id] && (
                                <Tooltip
                                  title={t("deploymentDetail.copyApiKey")}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      navigator.clipboard.writeText(
                                        revealedKeys[apiKey.id],
                                      );
                                      toast.success(
                                        t("deploymentDetail.apiKeyCopied"),
                                      );
                                    }}
                                  >
                                    <CopyIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        {new Date(apiKey.created_at).toLocaleDateString(locale)}
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        {formatExpiryDate(apiKey.expires_at)}
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        <Chip
                          label={
                            apiKey.is_active
                              ? t("common.active")
                              : t("common.inactive")
                          }
                          color={apiKey.is_active ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        {apiKey.is_active && (
                          <Tooltip title={t("deploymentDetail.deleteApiKey")}>
                            <IconButton
                              color="error"
                              onClick={() => {
                                setSelectedKey(apiKey);
                                setDeleteKeyDialogOpen(true);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary" paragraph>
                {t("deploymentDetail.noApiKeysForDeployment")}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateKeyDialogOpen(true)}
              >
                {t("deploymentDetail.createFirstApiKey")}
              </Button>
            </Paper>
          )}
        </TabPanel>

        {/* Timeline Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box
            sx={{ paddingInlineStart: 0, pt: 2, paddingInlineEnd: 2, pb: 2 }}
          >
            {" "}
            {/* Removed left padding here */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                mb: 3,
                mt: 0,
              }}
            >
              {" "}
              {/* Changed justifyContent and added mt: 0 */}
              {/* Typography Title Removed */}
              <Button
                variant="contained"
                startIcon={<RefreshIcon className="rtl:scale-x-[-1]" />}
                onClick={fetchTimeline}
                disabled={timelineLoading}
                size="small" /* Make button smaller */
                sx={{ minWidth: "90px" }} /* Constrain minimum width */
              >
                {t("deploymentDetail.refreshTimeline")}
              </Button>
            </Box>
            {(() => {
              if (timelineLoading) {
                return <TimelineEventsSkeleton />;
              } else if (timeline.length > 0) {
                return (
                  <Timeline
                    position="right"
                    sx={{
                      p: 0,
                      paddingInlineStart: 0,
                      marginInlineEnd: 2,
                      m: 0,
                    }}
                  >
                    {(() => {
                      // Group timeline events by step
                      const groupedEvents = {};
                      timeline.forEach((event) => {
                        if (!groupedEvents[event.step]) {
                          groupedEvents[event.step] = [];
                        }
                        groupedEvents[event.step].push(event);
                      });

                      // Use the order from the API response - get unique steps in the order they appear
                      const orderedSteps = [];
                      timeline.forEach((event) => {
                        if (!orderedSteps.includes(event.step)) {
                          orderedSteps.push(event.step);
                        }
                      });

                      return orderedSteps.map((step, stepIndex) => {
                        // Get events for this step, sorted by timestamp (latest first)
                        const events = groupedEvents[step].sort(
                          (a, b) =>
                            new Date(b.timestamp) - new Date(a.timestamp),
                        );

                        // Determine the overall step status based on all events in this step
                        let stepStatus = "STARTED"; // Default to started

                        // Check if any event has SUCCESS status - if so, the step is successful
                        const hasSuccess = events.some(
                          (event) => event.status === "SUCCESS",
                        );
                        const hasFailed = events.some(
                          (event) =>
                            event.status === "FAILED" ||
                            event.status === "ERROR",
                        );

                        if (hasFailed) {
                          stepStatus = "FAILED";
                        } else if (hasSuccess) {
                          stepStatus = "SUCCESS";
                        } else {
                          // If no success or failure, check the latest event status
                          stepStatus = events[0].status || "STARTED";
                        }

                        // Use the most meaningful event to represent the step in collapsed view
                        // Priority: SUCCESS message > FAILED message > Latest message
                        let representativeEvent = events[0]; // Default to latest

                        if (hasSuccess) {
                          // Find the SUCCESS event
                          representativeEvent =
                            events.find(
                              (event) => event.status === "SUCCESS",
                            ) || events[0];
                        } else if (hasFailed) {
                          // Find the FAILED event
                          representativeEvent =
                            events.find(
                              (event) =>
                                event.status === "FAILED" ||
                                event.status === "ERROR",
                            ) || events[0];
                        }

                        const isExpanded = !!expandedSteps[step];

                        // Determine icon and color based on the latest step status
                        const getStatusIcon = (status) => {
                          switch (status) {
                            case "SUCCESS":
                              return <CheckCircleIcon fontSize="small" />;
                            case "STARTED":
                              return <PendingIcon fontSize="small" />;
                            case "PENDING":
                              return <PendingIcon fontSize="small" />;
                            case "RUNNING":
                              return <PendingIcon fontSize="small" />;
                            case "FAILED":
                            case "ERROR":
                              return <ErrorIcon fontSize="small" />;
                            case "WARNING":
                              return <WarningIcon fontSize="small" />;
                            default:
                              return <AccessTimeIcon fontSize="small" />;
                          }
                        };

                        const getStatusColor = (status) => {
                          if (!status) return "default";
                          switch (status) {
                            case "SUCCESS":
                              return "success";
                            case "STARTED":
                              return "info";
                            case "PENDING":
                              return "warning";
                            case "RUNNING":
                              return "info";
                            case "FAILED":
                            case "ERROR":
                              return "error";
                            case "WARNING":
                              return "warning";
                            default:
                              return "default";
                          }
                        };

                        const finalStepStatusColor = getStatusColor(stepStatus);

                        return (
                          <TimelineItem
                            key={step}
                            sx={{
                              minHeight: "auto",
                              my: 0.5,
                              "&::before": { display: "none" },
                            }}
                          >
                            <TimelineOppositeContent
                              sx={{
                                flex: "none", // Remove flex
                                width: "60px", // Set fixed minimal width
                                maxWidth: "60px", // Enforce max width
                                textAlign: "end", // Align text
                                color: "text.secondary",
                                pt: "10px", // Align better with the dot
                                fontSize: "0.75rem", // Smaller timestamp
                                m: 0,
                                paddingInlineEnd: 1,
                                paddingInlineStart: 0,
                              }}
                            >
                              {new Date(
                                representativeEvent.timestamp,
                              ).toLocaleTimeString(locale, {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                              <br />
                              {new Date(
                                representativeEvent.timestamp,
                              ).toLocaleDateString(locale, {
                                month: "short",
                                day: "numeric",
                              })}
                            </TimelineOppositeContent>

                            <TimelineSeparator sx={{ mx: 0 }}>
                              {" "}
                              {/* Remove horizontal margin */}
                              <TimelineDot
                                color={finalStepStatusColor}
                                variant={
                                  stepStatus === "RUNNING" ||
                                  stepStatus === "PENDING" ||
                                  stepStatus === "STARTED"
                                    ? "outlined"
                                    : "filled"
                                } // Outline for ongoing steps
                                sx={{ my: 0.5, boxShadow: "none" }}
                              >
                                {getStatusIcon(stepStatus)}
                              </TimelineDot>
                              {stepIndex < orderedSteps.length - 1 && (
                                <TimelineConnector
                                  sx={{
                                    height: "auto",
                                    minHeight: 30,
                                    bgcolor: "divider",
                                  }}
                                />
                              )}
                            </TimelineSeparator>

                            <TimelineContent
                              sx={{
                                paddingInlineEnd: 0,
                                py: 0.5,
                                paddingInlineStart: 1,
                              }}
                            >
                              {" "}
                              {/* Adjust left padding */}
                              <Paper
                                elevation={0} // Less emphasis
                                sx={{
                                  p: 1.5,
                                  mb: 1,
                                  border: 1,
                                  borderColor: "divider", // Use divider color for consistency
                                  borderRadius: 1,
                                  transition: "all 0.3s",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => toggleStepExpanded(step)}
                                >
                                  {/* Left side: Step Name */}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      fontWeight="medium"
                                      sx={{ fontSize: "0.9rem" }}
                                    >
                                      {formatStepName(step, t)}
                                    </Typography>
                                  </Box>
                                  {/* Right side: Status Chip & Expander */}
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Chip
                                      label={getDeploymentStatusLabel(
                                        stepStatus,
                                      )}
                                      color={finalStepStatusColor}
                                      size="small"
                                      variant="outlined" // Subtle chip
                                      sx={{
                                        marginInlineEnd: 1,
                                        height: "22px",
                                        fontSize: "0.7rem",
                                        fontWeight: "medium",
                                      }}
                                    />
                                    {isExpanded ? (
                                      <ExpandLessIcon fontSize="small" />
                                    ) : (
                                      <ExpandMoreIcon fontSize="small" />
                                    )}
                                  </Box>
                                </Box>

                                {/* Always show representative message slightly dimmed */}
                                <Typography
                                  variant="body2"
                                  sx={{
                                    mt: 0.5,
                                    fontSize: "0.8rem",
                                    color: "text.secondary",
                                  }}
                                >
                                  {representativeEvent.message}
                                </Typography>

                                <Collapse in={isExpanded}>
                                  {events.length > 0 && ( // Show history if there are events
                                    <Box
                                      sx={{
                                        mt: 1.5,
                                        borderTop: 1,
                                        borderColor: "divider",
                                        pt: 1,
                                      }}
                                    >
                                      {events.map((event, idx) => {
                                        const isHealthCheck = event.message
                                          ?.toLowerCase()
                                          .includes("health check");
                                        return (
                                          <Box
                                            key={idx}
                                            sx={{
                                              display: "flex",
                                              justifyContent: "space-between",
                                              alignItems: "center",
                                              py: 0.25, // Reduced padding
                                              opacity: isHealthCheck ? 0.7 : 1, // Dim health checks
                                            }}
                                          >
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                fontSize: "0.75rem",
                                                color: isHealthCheck
                                                  ? "text.disabled"
                                                  : "text.secondary", // Further dim health check messages
                                              }}
                                            >
                                              {event.message}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              color="text.disabled"
                                              sx={{
                                                fontSize: "0.65rem",
                                                whiteSpace: "nowrap",
                                                paddingInlineStart: 1,
                                              }}
                                            >
                                              {new Date(
                                                event.timestamp,
                                              ).toLocaleTimeString(locale, {
                                                hour: "2-digit",
                                                minute: "2-digit",
                                                second: "2-digit",
                                              })}
                                            </Typography>
                                          </Box>
                                        );
                                      })}
                                    </Box>
                                  )}
                                </Collapse>
                              </Paper>
                            </TimelineContent>
                          </TimelineItem>
                        );
                      });
                    })()}
                  </Timeline>
                );
              } else {
                return (
                  <Paper sx={{ p: 4, textAlign: "center" }}>
                    <Typography color="text.secondary">
                      {t("deploymentDetail.noTimelineEventsAvailable")}
                    </Typography>
                  </Paper>
                );
              }
            })()}
          </Box>
        </TabPanel>

        {/* Versions Tab */}
        {/* <TabPanel value={tabValue} index={3}>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Model Versions
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenVersionDialog}
              disabled={loading}
            >
              Create New Version
            </Button>
          </Box>

          {loading ? (
            <VersionsTableSkeleton />
          ) : versions.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell align={isRTL ? "right" : "left"}>{t("common.version")}</TableCell>
                    <TableCell align={isRTL ? "right" : "left"}>{t("common.created")}</TableCell>
                    <TableCell align={isRTL ? "right" : "left"}>{t("common.status")}</TableCell>
                    <TableCell align={isRTL ? "right" : "left"}>{t("deploymentDetail.modelSource")}</TableCell>
                    <TableCell align={isRTL ? "right" : "left"}>{t("deploymentDetail.modelUrl")}</TableCell>
                    <TableCell align={isRTL ? "right" : "left"}>{t("common.actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {versions.map((version) => (
                    <TableRow key={version.version}>
                      <TableCell align={isRTL ? "right" : "left"}>
                        {version.version}
                        {version.is_active && (
                          <Chip
                            label={t("common.active")}
                            color="primary"
                            size="small"
                            sx={{ marginInlineStart: 1 }}
                          />
                        )}
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        {formatDate(version.created_at)}
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        <Chip
                          label={version.status || 'Unknown'}
                          color={getStatusColor(version.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        {version.model_source === 'hf' ? 'Hugging Face' :
                          version.model_source === 's3' ? 'S3 Bucket' :
                            version.model_source === 'http' ? 'HTTP URL' :
                              version.model_source}
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        <Typography variant="body2" sx={{
                          maxWidth: '200px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {version.model_url}
                        </Typography>
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        {!version.is_active && version.status === 'ready' && (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleActivateVersion(version.version)}
                          >
                            Activate
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary" paragraph>
                No versions found for this deployment.
              </Typography>
              {!loading && deployment && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenVersionDialog}
                >
                  Create New Version
                </Button>
              )}
            </Paper>
          )}
        </TabPanel> */}

        {/* Schedules Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box
            sx={{
              mb: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">
              {t("deploymentDetail.uptimeSchedules")}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setScheduleUptimeDialogOpen(true)}
            >
              {t("deploymentDetail.createNewSchedule")}
            </Button>
          </Box>

          {schedulesLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : schedules.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell align={isRTL ? "right" : "left"}>
                      {t("common.name")}
                    </TableCell>
                    <TableCell align={isRTL ? "right" : "left"}>
                      {t("common.type")}
                    </TableCell>
                    <TableCell align={isRTL ? "right" : "left"}>
                      {t("common.time")}
                    </TableCell>
                    <TableCell align={isRTL ? "right" : "left"}>
                      {t("deploymentDetail.days")}
                    </TableCell>
                    <TableCell align={isRTL ? "right" : "left"}>
                      {t("schedule.timezone")}
                    </TableCell>
                    <TableCell align={isRTL ? "right" : "left"}>
                      {t("common.active")}
                    </TableCell>
                    <TableCell align={isRTL ? "right" : "left"}>
                      {t("deploymentDetail.nextExecution")}
                    </TableCell>
                    <TableCell align={isRTL ? "right" : "left"}>
                      {t("common.actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {schedules.map((schedule) => (
                    <TableRow
                      key={schedule.id}
                      sx={{
                        opacity: schedule.is_active ? 1 : 0.7,
                        backgroundColor: schedule.is_active
                          ? "inherit"
                          : "rgba(0, 0, 0, 0.02)",
                      }}
                    >
                      <TableCell align={isRTL ? "right" : "left"}>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {schedule.name}
                          </Typography>
                          {schedule.description && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {schedule.description}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        <Chip
                          label={formatScheduleType(schedule.schedule_type)}
                          variant="outlined"
                          size="small"
                        />
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        <Typography variant="body2">
                          {schedule.start_time} - {schedule.end_time}
                        </Typography>
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        <Typography variant="body2">
                          {formatDaysOfWeek(schedule.days_of_week)}
                        </Typography>
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        <Typography variant="body2">
                          {schedule.timezone}
                        </Typography>
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        <Switch
                          className="ms-[-12px]"
                          checked={schedule.is_active}
                          onChange={() => handleToggleScheduleActive(schedule)}
                          disabled={updatingSchedules[schedule.id]}
                          color="primary"
                          size="small"
                        />
                        {updatingSchedules[schedule.id] && (
                          <CircularProgress
                            size={16}
                            sx={{ marginInlineStart: 1 }}
                          />
                        )}
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        <Typography
                          variant="body2"
                          color={
                            schedule.next_execution
                              ? "primary"
                              : "text.secondary"
                          }
                        >
                          {schedule.next_execution
                            ? new Date(schedule.next_execution).toLocaleString(
                                locale,
                              )
                            : t("common.notAvailable")}
                        </Typography>
                      </TableCell>
                      <TableCell align={isRTL ? "right" : "left"}>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Tooltip title={t("deploymentDetail.editSchedule")}>
                            <IconButton
                              size="small"
                              onClick={() => handleEditSchedule(schedule)}
                            >
                              <SettingsIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t("deploymentDetail.deleteSchedule")}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteSchedule(schedule)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <ScheduleIcon
                sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
              />
              <Typography color="text.secondary" paragraph>
                {t("deploymentDetail.noSchedulesForDeployment")}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setScheduleUptimeDialogOpen(true)}
              >
                {t("deploymentDetail.createFirstSchedule")}
              </Button>
            </Paper>
          )}
        </TabPanel>
      </Box>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>{t("deploymentDetail.deleteDeployment")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("deployments.deleteDeploymentConfirm", {
              name: deployment?.name,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteCancel}
            disabled={deleteLoading}
            color="secondary"
            variant="text"
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            disabled={deleteLoading}
            startIcon={
              deleteLoading ? <CircularProgress size={20} /> : <DeleteIcon />
            }
          >
            {deleteLoading ? t("common.deleting") : t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create API key dialog */}
      <Dialog open={createKeyDialogOpen} onClose={handleCreateKeyCancel}>
        <DialogTitle>{t("deploymentDetail.createApiKey")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t("deploymentDetail.createApiKeyDescription")}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label={t("deploymentDetail.descriptionOptional")}
            fullWidth
            variant="outlined"
            value={newKeyDescription}
            onChange={(e) => setNewKeyDescription(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t("deploymentDetail.expiresInDays")}
            fullWidth
            variant="outlined"
            type="text"
            value={newKeyExpiry}
            inputProps={{
              inputMode: "numeric", // mobile numeric keypad
              pattern: "[0-9]*",
              min: 1,
            }}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "");
              setNewKeyExpiry(value);
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCreateKeyCancel}
            disabled={createKeyLoading}
            color="secondary"
            variant="text"
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleCreateKeyConfirm}
            color="primary"
            variant="contained"
            disabled={createKeyLoading}
            startIcon={
              createKeyLoading ? <CircularProgress size={20} /> : <KeyIcon />
            }
          >
            {createKeyLoading ? t("common.creating") : t("apiKeys.createKey")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete API key dialog */}
      <Dialog open={deleteKeyDialogOpen} onClose={handleDeleteKeyCancel}>
        <DialogTitle>{t("deploymentDetail.deleteApiKey")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("deploymentDetail.deleteApiKeyConfirm")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteKeyCancel}
            disabled={deleteKeyLoading}
            color="secondary"
            variant="text"
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleDeleteKeyConfirm}
            color="error"
            disabled={deleteKeyLoading}
            startIcon={
              deleteKeyLoading ? <CircularProgress size={20} /> : <DeleteIcon />
            }
          >
            {deleteKeyLoading ? t("common.deleting") : t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Version Dialog */}
      <Dialog
        open={createVersionDialogOpen}
        onClose={() => setCreateVersionDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t("deploymentDetail.createVersion")}</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Stepper activeStep={versionFormStep}>
              <Step>
                <StepLabel>{t("createDeployment.modelDetails")}</StepLabel>
              </Step>
              <Step>
                <StepLabel>
                  {t("deploymentDetail.resourceConfiguration")}
                </StepLabel>
              </Step>
              <Step>
                <StepLabel>{t("deploymentDetail.credentials")}</StepLabel>
              </Step>
              <Step>
                <StepLabel>{t("deploymentDetail.reviewAndDeploy")}</StepLabel>
              </Step>
            </Stepper>
          </Box>

          {/* Step 1: Model Details */}
          {versionFormStep === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {t("createDeployment.modelDetails")}
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label={t("deploymentDetail.deploymentName")}
                    value={versionForm.name}
                    onChange={(e) =>
                      handleVersionFormChange("name", e.target.value)
                    }
                    fullWidth
                    margin="normal"
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <FormLabel component="legend">{t("common.type")}</FormLabel>
                    <RadioGroup
                      row
                      value={versionForm.is_llm ? "llm" : "deeplearning"}
                      onChange={(e) =>
                        handleVersionFormChange(
                          "is_llm",
                          e.target.value === "llm",
                        )
                      }
                    >
                      <FormControlLabel
                        value="llm"
                        control={<Radio />}
                        label={t("deployments.llm")}
                      />
                      <FormControlLabel
                        value="deeplearning"
                        control={<Radio />}
                        label={t("deploymentDetail.deepLearningModel")}
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <FormLabel component="legend">
                      {t("deployments.modelSource")}
                    </FormLabel>
                    <RadioGroup
                      row
                      value={versionForm.model_source}
                      onChange={(e) =>
                        handleVersionFormChange("model_source", e.target.value)
                      }
                    >
                      <FormControlLabel
                        value="hf"
                        control={<Radio />}
                        label={t("deployments.huggingFace")}
                      />
                      <FormControlLabel
                        value="s3"
                        control={<Radio />}
                        label={t("deployments.s3Bucket")}
                      />
                      <FormControlLabel
                        value="http"
                        control={<Radio />}
                        label={t("deployments.httpUrl")}
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    className="url-textfield"
                    label={t("deployments.modelUrl")}
                    value={versionForm.model_url}
                    onChange={(e) =>
                      handleVersionFormChange("model_url", e.target.value)
                    }
                    fullWidth
                    margin="normal"
                    required
                    helperText={
                      versionForm.model_source === "hf"
                        ? t("deploymentDetail.huggingFaceModelUrlHelper")
                        : versionForm.model_source === "s3"
                          ? t("deploymentDetail.s3ModelUrlHelper")
                          : t("deploymentDetail.httpModelUrlHelper")
                    }
                  />
                </Grid>

                {/* Model-specific configuration */}
                {versionForm.is_llm ? (
                  <Grid item xs={12}>
                    <Typography gutterBottom>
                      {t("deploymentDetail.llmConfiguration")}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={versionForm.llm.speculative_model}
                          onChange={(e) =>
                            handleVersionFormChange(
                              "llm.speculative_model",
                              e.target.checked,
                            )
                          }
                        />
                      }
                      label={t("deploymentDetail.useSpeculativeDecoding")}
                    />
                  </Grid>
                ) : (
                  <Grid item xs={12}>
                    <Typography gutterBottom>
                      {t("deploymentDetail.deepLearningConfiguration")}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label={t("deploymentDetail.inputSize")}
                          value={versionForm.deeplearning.input_size}
                          onChange={(e) =>
                            handleVersionFormChange(
                              "deeplearning.input_size",
                              e.target.value,
                            )
                          }
                          fullWidth
                          margin="normal"
                          helperText={t("deploymentDetail.inputSizeHelper")}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label={t("deploymentDetail.maxBatchSize")}
                          type="number"
                          value={versionForm.deeplearning.max_batch_size}
                          onChange={(e) =>
                            handleVersionFormChange(
                              "deeplearning.max_batch_size",
                              parseInt(e.target.value),
                            )
                          }
                          fullWidth
                          margin="normal"
                          helperText={t("deploymentDetail.maxBatchSizeHelper")}
                          InputProps={{ inputProps: { min: 1 } }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          label={t("deploymentDetail.latencyConstraint")}
                          type="number"
                          value={versionForm.deeplearning.latency_constraint}
                          onChange={(e) =>
                            handleVersionFormChange(
                              "deeplearning.latency_constraint",
                              parseInt(e.target.value),
                            )
                          }
                          fullWidth
                          margin="normal"
                          helperText={t(
                            "deploymentDetail.latencyConstraintHelper",
                          )}
                          InputProps={{ inputProps: { min: 0 } }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          label={t("deploymentDetail.throughputConstraint")}
                          type="number"
                          value={versionForm.deeplearning.throughput_constraint}
                          onChange={(e) =>
                            handleVersionFormChange(
                              "deeplearning.throughput_constraint",
                              parseInt(e.target.value),
                            )
                          }
                          fullWidth
                          margin="normal"
                          helperText={t(
                            "deploymentDetail.throughputConstraintHelper",
                          )}
                          InputProps={{ inputProps: { min: 0 } }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          label={t("deploymentDetail.gpuMemoryUsage")}
                          type="number"
                          value={versionForm.deeplearning.gpu_used_memory}
                          onChange={(e) =>
                            handleVersionFormChange(
                              "deeplearning.gpu_used_memory",
                              parseInt(e.target.value),
                            )
                          }
                          fullWidth
                          margin="normal"
                          helperText={t(
                            "deploymentDetail.gpuMemoryUsageHelper",
                          )}
                          InputProps={{ inputProps: { min: 0 } }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          {/* Step 2: Resource Configuration */}
          {versionFormStep === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {t("deploymentDetail.resourceConfiguration")}
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label={t("deploymentDetail.maxReplicas")}
                    type="number"
                    value={versionForm.max_replicas}
                    onChange={(e) =>
                      handleVersionFormChange(
                        "max_replicas",
                        parseInt(e.target.value),
                      )
                    }
                    fullWidth
                    margin="normal"
                    helperText={t("deploymentDetail.maxReplicasHelper")}
                    InputProps={{ inputProps: { min: 1, max: 10 } }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>{t("deployments.gpuCount")}</InputLabel>
                    <Select
                      value={versionForm.gpu_count}
                      onChange={(e) =>
                        handleVersionFormChange("gpu_count", e.target.value)
                      }
                      label={t("deployments.gpuCount")}
                    >
                      <MenuItem value={1}>
                        {t("deploymentDetail.gpuOption", { value: 1 })}
                      </MenuItem>
                      <MenuItem value={2}>
                        {t("deploymentDetail.gpuOption", { value: 2 })}
                      </MenuItem>
                      <MenuItem value={4}>
                        {t("deploymentDetail.gpuOption", { value: 4 })}
                      </MenuItem>
                      <MenuItem value={8}>
                        {t("deploymentDetail.gpuOption", { value: 8 })}
                      </MenuItem>
                    </Select>
                    <FormHelperText>
                      {t("deploymentDetail.gpuCountHelper")}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label={t("deploymentDetail.memoryRequest")}
                    value={versionForm.memory_request}
                    onChange={(e) =>
                      handleVersionFormChange("memory_request", e.target.value)
                    }
                    fullWidth
                    margin="normal"
                    helperText={t("deploymentDetail.memoryRequestHelper")}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label={t("deploymentDetail.memoryLimit")}
                    value={versionForm.memory_limit}
                    onChange={(e) =>
                      handleVersionFormChange("memory_limit", e.target.value)
                    }
                    fullWidth
                    margin="normal"
                    helperText={t("deploymentDetail.memoryLimitHelper")}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label={t("deploymentDetail.cpuRequest")}
                    value={versionForm.cpu_request}
                    onChange={(e) =>
                      handleVersionFormChange("cpu_request", e.target.value)
                    }
                    fullWidth
                    margin="normal"
                    helperText={t("deploymentDetail.cpuRequestHelper")}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label={t("deploymentDetail.cpuLimit")}
                    value={versionForm.cpu_limit}
                    onChange={(e) =>
                      handleVersionFormChange("cpu_limit", e.target.value)
                    }
                    fullWidth
                    margin="normal"
                    helperText={t("deploymentDetail.cpuLimitHelper")}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Step 3: Credentials */}
          {versionFormStep === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {t("deploymentDetail.credentials")}
              </Typography>

              <Alert severity="info" sx={{ mb: 2 }}>
                {t("deploymentDetail.provideCredentialsIfNeeded")}
              </Alert>

              {versionForm.model_source === "hf" && (
                <TextField
                  label={t("deploymentDetail.huggingFaceToken")}
                  value={versionForm.hf_token}
                  onChange={(e) =>
                    handleVersionFormChange("hf_token", e.target.value)
                  }
                  fullWidth
                  margin="normal"
                  type="password"
                  helperText={t("deploymentDetail.huggingFaceTokenHelper")}
                />
              )}

              {versionForm.model_source === "s3" && (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t("deploymentDetail.awsAccessKeyId")}
                      value={versionForm.aws_access_key_id}
                      onChange={(e) =>
                        handleVersionFormChange(
                          "aws_access_key_id",
                          e.target.value,
                        )
                      }
                      fullWidth
                      margin="normal"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t("deploymentDetail.awsSecretAccessKey")}
                      value={versionForm.aws_secret_access_key}
                      onChange={(e) =>
                        handleVersionFormChange(
                          "aws_secret_access_key",
                          e.target.value,
                        )
                      }
                      fullWidth
                      margin="normal"
                      type="password"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t("deploymentDetail.awsRegion")}
                      value={versionForm.aws_region}
                      onChange={(e) =>
                        handleVersionFormChange("aws_region", e.target.value)
                      }
                      fullWidth
                      margin="normal"
                      placeholder="us-east-1"
                    />
                  </Grid>
                </Grid>
              )}
            </Box>
          )}

          {/* Step 4: Review & Deploy */}
          {versionFormStep === 3 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {t("deploymentDetail.reviewAndDeploy")}
              </Typography>

              <Alert severity="info" sx={{ mb: 3 }}>
                {t("deploymentDetail.reviewVersion")}
              </Alert>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        {t("createDeployment.modelDetails")}
                      </Typography>
                      <Typography variant="body2">
                        <strong>{t("deploymentDetail.nameLabel")}</strong>{" "}
                        {versionForm.name}
                      </Typography>
                      <Typography variant="body2">
                        <strong>{t("deploymentDetail.typeLabel")}</strong>{" "}
                        {versionForm.is_llm
                          ? t("deployments.llm")
                          : t("deploymentDetail.deepLearningModel")}
                      </Typography>
                      <Typography variant="body2">
                        <strong>{t("deploymentDetail.sourceLabel")}</strong>{" "}
                        {getModelSourceLabel(versionForm.model_source)}
                      </Typography>
                      <Typography variant="body2">
                        <strong>{t("deploymentDetail.urlLabel")}</strong>{" "}
                        {versionForm.model_url}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        {t("deploymentDetail.resources")}
                      </Typography>
                      <Typography variant="body2">
                        <strong>
                          {t("deploymentDetail.maxReplicasLabel")}
                        </strong>{" "}
                        {versionForm.max_replicas}
                      </Typography>
                      <Typography variant="body2">
                        <strong>{t("deploymentDetail.gpuCountLabel")}</strong>{" "}
                        {versionForm.gpu_count}
                      </Typography>
                      <Typography variant="body2">
                        <strong>{t("deploymentDetail.memoryLabel")}</strong>{" "}
                        {versionForm.memory_request} -{" "}
                        {versionForm.memory_limit}
                      </Typography>
                      <Typography variant="body2">
                        <strong>{t("deploymentDetail.cpuLabel")}</strong>{" "}
                        {versionForm.cpu_request} - {versionForm.cpu_limit}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCreateVersionDialogOpen(false)}
            disabled={createVersionLoading}
            color="secondary"
            variant="text"
          >
            {t("common.cancel")}
          </Button>

          {versionFormStep > 0 && (
            <Button
              onClick={() => setVersionFormStep((prev) => prev - 1)}
              disabled={createVersionLoading}
              variant="outlined"
            >
              {t("common.back")}
            </Button>
          )}

          {versionFormStep < 3 ? (
            <Button
              variant="contained"
              onClick={() => setVersionFormStep((prev) => prev + 1)}
            >
              {t("common.next")}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleCreateVersion}
              disabled={createVersionLoading}
              startIcon={
                createVersionLoading ? <CircularProgress size={20} /> : null
              }
            >
              {createVersionLoading
                ? t("common.creating")
                : t("deploymentDetail.createVersion")}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Register for RAG Dialog */}
      <RegisterModelForRagDialog
        open={registerForRagDialogOpen}
        onClose={() => {
          setRegisterForRagDialogOpen(false);
          // Refresh the registration status after closing the dialog
          checkModelRegistration();
        }}
        deployment={deployment}
        availableApiKeys={activeApiKeys}
        endpointUrl={getModelEndpointUrl()}
      />

      {/* Schedule Uptime Dialog */}
      <ScheduleUptimeDialog
        open={scheduleUptimeDialogOpen}
        onClose={() => setScheduleUptimeDialogOpen(false)}
        deployment={deployment}
        onRefresh={fetchSchedules}
      />

      {/* Edit Schedule Dialog */}
      <ScheduleUptimeDialog
        open={editScheduleDialogOpen}
        onClose={() => {
          setEditScheduleDialogOpen(false);
          setSelectedSchedule(null);
        }}
        deployment={deployment}
        onRefresh={fetchSchedules}
        editingSchedule={selectedSchedule}
      />

      {/* Delete Schedule Dialog */}
      <Dialog
        open={deleteScheduleDialogOpen}
        onClose={handleDeleteScheduleCancel}
      >
        <DialogTitle>{t("deploymentDetail.deleteSchedule")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("deploymentDetail.deleteScheduleConfirm", {
              name: selectedSchedule?.name,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteScheduleCancel}
            disabled={deleteScheduleLoading}
            color="secondary"
            variant="text"
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleDeleteScheduleConfirm}
            color="error"
            disabled={deleteScheduleLoading}
            startIcon={
              deleteScheduleLoading ? (
                <CircularProgress size={20} />
              ) : (
                <DeleteIcon />
              )
            }
          >
            {deleteScheduleLoading ? t("common.deleting") : t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

const stepKeyMap = {
  ACTIVE: "common.active",
  ARCHIVED: "deployments.archived",
  COMPLETED: "deploymentDetail.completed",
  ERROR: "common.error",
  FAILED: "deployments.failed",
  INITIALIZING: "deploymentDetail.initializing",
  INACTIVE: "common.inactive",
  DEPLOYING: "deploymentDetail.deploying",
  DOWNLOADING: "deploymentDetail.downloading",
  RESUMED: "deploymentDetail.resumed",
  LOADING: "deploymentDetail.loading",
  PAUSED: "deployments.paused",
  PENDING: "deployments.pending",
  READY: "deploymentDetail.ready",
  RUNNING: "deployments.running",
  STARTED: "deployments.started",
  STARTING: "deployments.starting",
  STOPPED: "deployments.stopped",
  SUCCESS: "deployments.success",
  WARNING: "deploymentDetail.warning",
};

const formatStepName = (step, t) => {
  if (!step) return t("common.unknown");

  const normalizedStep = step.toUpperCase();

  if (stepKeyMap[normalizedStep]) {
    return t(stepKeyMap[normalizedStep]);
  }

  return normalizedStep
    .split("_")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");
};

export default DeploymentDetail;
